// EosManager/resolve.ts
// Note: keep this file free of React/UI. Presentation + decision resolution.

import {
  getRecommendation,
  type ClinicalCondition,
  type Recommendation,
} from "./recommendations";

export type EosPerClinicalCondition = Record<string, string | number>;

export type ResolvedClinicalCondition = {
  clinicalCondition: ClinicalCondition;
  recommendation: Recommendation;
  color: "none" | "red" | "green" | "yellow";
  clinicalText: string;
  trackingText: string;
};

function getClinicalConditionColor(params: {
  recommendation: Recommendation;
  hasTemperature: boolean;
}): ResolvedClinicalCondition["color"] {
  const { recommendation, hasTemperature } = params;

  if (!hasTemperature) return "none";
  if (recommendation === "ANTIBIOTICS_VITALS_PER_NICU") return "red";
  if (recommendation === "ROUTINE_VITALS") return "green";
  return "yellow";
}

function getClinicalRecommendationText(params: {
  recommendation: Recommendation;
  hasTemperature: boolean;
}): string {
  const { recommendation, hasTemperature } = params;

  if (!hasTemperature) return "";
  if (recommendation === "ANTIBIOTICS_VITALS_PER_NICU") {
    return "טיפול אנטיביוטי אמפירי";
  }
  if (recommendation === "BLOOD_CULTURE_VITALS_EVERY_4_HOURS") {
    return "תרביות דם";
  }
  return "ללא תרביות, ללא טיפול אנטיביוטי";
}

function getTrackingRecommendationText(params: {
  recommendation: Recommendation;
  hasTemperature: boolean;
}): string {
  const { recommendation, hasTemperature } = params;

  if (!hasTemperature) return "none";
  if (recommendation === "ANTIBIOTICS_VITALS_PER_NICU") {
    return "ניטור רציף";
  }
  if (recommendation === "ROUTINE_VITALS") {
    return "מעקב שגרתי";
  }
  return "סימנים חיוניים כל 4 שעות למשך 24 שעות";
}

export function resolveClinicalCondition(params: {
  clinicalCondition: ClinicalCondition;
  eosPerClinicalCondition: EosPerClinicalCondition;
  eos: number;
  temperature?: number;
  minTemperatureBeforeForcingAntibiotics: number;
}): ResolvedClinicalCondition {
  const {
    clinicalCondition,
    eosPerClinicalCondition,
    eos,
    temperature,
    minTemperatureBeforeForcingAntibiotics,
  } = params;

  const hasTemperature = temperature !== undefined && temperature !== null;

  const recommendation = getRecommendation({
    clinicalCondition,
    eosPerClinicalCondition,
    eos,
    temperature,
    minTemperatureBeforeForcingAntibiotics,
  });

  return {
    clinicalCondition,
    recommendation,
    color: getClinicalConditionColor({ recommendation, hasTemperature }),
    clinicalText: getClinicalRecommendationText({ recommendation, hasTemperature }),
    trackingText: getTrackingRecommendationText({ recommendation, hasTemperature }),
  };
}